import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, Inject, OnInit, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatDatepickerInputEvent, MatDatepickerModule} from '@angular/material/datepicker';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import * as _ from 'lodash';
import * as moment from 'moment-timezone';
import {BehaviorSubject} from 'rxjs';
import {colors} from 'src/app/app.constants';
import {Project} from 'src/app/core/model/project';
import {CreateProjectAction, PatchProjectAction} from 'src/app/core/store/projects/projects.action';
import {TimezoneService} from 'src/app/services/timezone.service';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphCheckboxComponent} from 'src/app/shared/design/oph-checkbox/oph-checkbox.component';
import {OphInputsModule} from 'src/app/shared/design/oph-inputs/oph-inputs.module';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';
import {CustomHeaderComponent} from '../builder-calendar/builder-calendar.component';
import {EMPTY_CHALLENGE, EMPTY_PROJECT} from '../../shared/projects-constants';

export enum DialogView {
  Selection = 'selection',
  Challenge = 'challenge',
  Project = 'project',
}

export interface ProjectsDialogData {
  project?: Project;
  view?: DialogView;
}

@Component({
  selector: 'projects-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatDatepickerModule,
    MatMenuModule,
    OphButtonModule,
    OphInputsModule,
    OphCheckboxComponent,
    OphXButtonComponent,
  ],
  templateUrl: './projects-dialog.component.html',
  styleUrl: './projects-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsDialogComponent implements OnInit {
  readonly customHeader = CustomHeaderComponent;
  readonly colors: string[] = colors;
  readonly DialogView = DialogView;

  currentView$ = new BehaviorSubject<DialogView>(DialogView.Selection);
  selectedType$ = new BehaviorSubject<'challenge' | 'project' | null>(null);
  loading$ = new BehaviorSubject<boolean>(false);

  // Challenge fields
  cycleLengthOptions = [3, 4, 5, 7];
  selectedCycleLength = signal(7);

  // Project fields
  project: Project;
  projectNameMaxLength = signal(42);
  selectedColor = signal('#B8B8B8');
  projectName = signal('');
  today = signal<moment.Moment | null>(null);
  startDate = signal<moment.Moment | null>(null);
  endDate = signal<moment.Moment | null>(null);
  originalStartDate = signal<moment.Moment | null>(null);
  isDraft = signal(false);
  isEditing = signal(false);

  isExpired = computed(() => {
    const start = this.startDate();
    const end = this.endDate();
    const today = this.today();
    if (!start || !end || !today || this.isDraft()) return false;
    return !today.isBetween(start, end, 'day', '[]');
  });

  startDateMin = computed(() => {
    if (this.isExpired()) return this.originalStartDate();
    return this.today();
  });

  startDateDisplay = computed(() => {
    const date = this.startDate();
    if (!date) return 'Today';
    const today = moment().tz(this.timezoneService.getTimeZone()).startOf('day');
    if (date.isSame(today, 'day')) return 'Today';
    return date.format('MM/DD/YYYY');
  });

  endDateDisplay = computed(() => {
    const date = this.endDate();
    if (!date) return 'Ongoing';
    return date.format('MM/DD/YYYY');
  });

  endDateSet = computed(() => this.endDate() !== null);

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: ProjectsDialogData,
    private dialogRef: MatDialogRef<ProjectsDialogComponent>,
    private store$: Store,
    private router: Router,
    private timezoneService: TimezoneService
  ) {}

  ngOnInit(): void {
    this.today.set(moment().tz(this.timezoneService.getTimeZone()).startOf('day'));

    if (this.data?.project?._id) {
      // Edit mode - go directly to project view
      this.isEditing.set(true);
      this.project = _.cloneDeep(this.data.project);
      this.projectName.set(this.project.label);
      this.selectedColor.set(this.project.color || '#B8B8B8');
      this.isDraft.set(this.project.status === 'inactive');

      if (this.project.startDateTime) {
        const start = moment(this.project.startDateTime).tz(this.timezoneService.getTimeZone());
        this.startDate.set(start);
        this.originalStartDate.set(start.clone());
      }
      if (this.project.endDateTime && !this.project.noEndDate) {
        this.endDate.set(moment(this.project.endDateTime).tz(this.timezoneService.getTimeZone()));
      }

      this.currentView$.next(DialogView.Project);
    } else {
      // Create mode
      this.project = {...EMPTY_PROJECT};
      this.startDate.set(moment().tz(this.timezoneService.getTimeZone()).startOf('day'));

      if (this.data?.view) {
        this.currentView$.next(this.data.view);
        this.selectedType$.next(this.data.view === DialogView.Challenge ? 'challenge' : 'project');
      }
    }
  }

  onTypeSelect(type: 'challenge' | 'project'): void {
    this.selectedType$.next(type);

    // Wait for animation then change view
    setTimeout(() => {
      if (type === 'challenge') {
        this.currentView$.next(DialogView.Challenge);
      } else {
        this.currentView$.next(DialogView.Project);
      }
    }, 150);
  }

  onCycleLengthSelect(length: number): void {
    this.selectedCycleLength.set(length);
  }

  onProjectNameChange(name: string): void {
    this.projectName.set(name);
  }

  onColorSelect(color: string): void {
    this.selectedColor.set(color);
  }

  onStartDateChange(event: MatDatepickerInputEvent<moment.Moment>): void {
    const date = event.value;
    if (date) {
      const newStart = moment(date).tz(this.timezoneService.getTimeZone());
      this.startDate.set(newStart);

      const end = this.endDate();
      if (this.isExpired() && end && newStart.isAfter(end, 'day')) {
        this.endDate.set(newStart.clone().add(1, 'day'));
      }
    }
  }

  onEndDateChange(event: MatDatepickerInputEvent<moment.Moment>): void {
    const date = event.value;
    if (date) {
      const newEnd = moment(date).tz(this.timezoneService.getTimeZone());
      this.endDate.set(newEnd);

      const start = this.startDate();
      if (this.isExpired() && start && newEnd.isBefore(start, 'day')) {
        this.startDate.set(newEnd.clone().subtract(1, 'day'));
      }
    }
  }

  clearEndDate(): void {
    // Set to ongoing (1 year from start date)
    this.endDate.set(null);
  }

  onDraftChange(checked: boolean): void {
    this.isDraft.set(checked);
  }

  onBack(): void {
    const currentView = this.currentView$.value;
    if (currentView === DialogView.Challenge || currentView === DialogView.Project) {
      if (!this.isEditing()) {
        this.currentView$.next(DialogView.Selection);
        this.selectedType$.next(null);
      } else {
        this.dialogRef.close();
      }
    } else {
      this.dialogRef.close();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onNextChallenge(): void {
    this.loading$.next(true);

    const challenge: Project = {
      ...EMPTY_CHALLENGE,
      challengeBuilder: {
        ...EMPTY_CHALLENGE.challengeBuilder,
        cycleLength: this.selectedCycleLength(),
      },
      startDateTime: this.timezoneService.convertMomentToUtcString(
        moment().tz(this.timezoneService.getTimeZone()).startOf('day')
      ),
      endDateTime: this.timezoneService.convertMomentToUtcString(
        moment().tz(this.timezoneService.getTimeZone()).add(1, 'year').endOf('day')
      ),
      noEndDate: true,
      timezone: this.timezoneService.getTimeZone(),
    };

    this.store$.dispatch(
      new CreateProjectAction({
        project: challenge,
        onSuccess: (createdProject: Project) => {
          this.loading$.next(false);
          this.dialogRef.close();
          this.router.navigate(['/projects', 'challenge-builder', createdProject._id, 'goal']);
        },
        onFailure: () => {
          this.loading$.next(false);
        },
      })
    );
  }

  onNextProject(): void {
    this.loading$.next(true);

    const start = this.startDate() || moment().tz(this.timezoneService.getTimeZone()).startOf('day');
    const end = this.endDate() || start.clone().add(1, 'year');

    const projectData: Project = {
      ...this.project,
      label: this.projectName() || 'Untitled_1',
      color: this.selectedColor(),
      startDateTime: this.timezoneService.convertMomentToUtcString(start.startOf('day')),
      endDateTime: this.timezoneService.convertMomentToUtcString(end.endOf('day')),
      status: this.isDraft() ? 'inactive' : 'active',
      noEndDate: !this.endDate(),
      timezone: this.timezoneService.getTimeZone(),
    };

    if (this.isEditing() && this.project._id) {
      // Patch existing project
      this.store$.dispatch(
        new PatchProjectAction({
          project: projectData,
          onSuccess: (updatedProject: Project) => {
            this.loading$.next(false);
            this.dialogRef.close(updatedProject);
          },
          onFailure: () => {
            this.loading$.next(false);
          },
        })
      );
    } else {
      // Create new project
      this.store$.dispatch(
        new CreateProjectAction({
          project: projectData,
          onSuccess: (createdProject: Project) => {
            this.loading$.next(false);
            this.dialogRef.close(createdProject);
          },
          onFailure: () => {
            this.loading$.next(false);
          },
        })
      );
    }
  }
}
